export enum SlideQuestionType {
  /// Poll
  poll = 0,

  /// Multiple Choice
  multipleChoice = 1,

  /// title
  title = 2,

  /// Custom Poll
  customPoll = 3,

  /// sorting element
  sorting = 4,

  /// talk track
  talkTrack = 5,

  /// solo bot
  moderatorBot = 6,

  /// moderation task
  moderationTask = 7,

  /// tutorBot
  tutorBot = 8,

  /// graderBot
  graderBot = 9,

  /// sortable string list
  sortableStringList = 10,

  /// this allows you to see the moderation tasks completing in the meeting
  displayModerationTask = 11,

  // pop up instructions - a dialog which shows up before sortables
  popUpInstructions = 12,

  // solo role play bot
  rolePlayBot = 13,

  // solo form completion bot
  formCompletionBot = 14,

  // interactive form element
  interactiveFormElement = 15,

  // ai agent
  aiAgent = 16,

  // suggested timer for slide
  suggestedTimer = 17,
}

/**
 * The solo bot family: question types whose `question` text is spoken to the
 * student. Single source of truth — the SlideQuestion model's static, the
 * publish-time validator, and the checkTalkTrackAudio callable in functions/
 * all consume this list, so client and server can never disagree about which
 * types are narrated.
 */
export const soloBotQuestionTypes: readonly SlideQuestionType[] = [
  SlideQuestionType.moderatorBot,
  SlideQuestionType.tutorBot,
  SlideQuestionType.graderBot,
  SlideQuestionType.rolePlayBot,
  SlideQuestionType.formCompletionBot,
] as const

/**
 * Everything with narrated audio: the solo bots plus the talk track itself.
 */
export const narratedQuestionTypes: readonly SlideQuestionType[] = [
  SlideQuestionType.talkTrack,
  ...soloBotQuestionTypes,
] as const

// function the converts the JSON value to the SlideQuestionType
export function slideQuestionType(
  slideQuestionType: number
): SlideQuestionType {
  switch (slideQuestionType) {
    case 0:
      return SlideQuestionType.poll
    case 1:
      return SlideQuestionType.multipleChoice
    case 2:
      return SlideQuestionType.title
    case 3:
      return SlideQuestionType.customPoll
    case 4:
      return SlideQuestionType.sorting
    case 5:
      return SlideQuestionType.talkTrack
    case 6:
      return SlideQuestionType.moderatorBot
    case 7:
      return SlideQuestionType.moderationTask
    case 8:
      return SlideQuestionType.tutorBot
    case 9:
      return SlideQuestionType.graderBot
    case 10:
      return SlideQuestionType.sortableStringList
    case 11:
      return SlideQuestionType.displayModerationTask
    case 12:
      return SlideQuestionType.popUpInstructions
    case 13:
      return SlideQuestionType.rolePlayBot
    case 14:
      return SlideQuestionType.formCompletionBot
    case 15:
      return SlideQuestionType.interactiveFormElement
    case 16:
      return SlideQuestionType.aiAgent
    case 17:
      return SlideQuestionType.suggestedTimer
    default:
      throw new Error('Unhandled SlideQuestionType')
  }
}

// Function to get JSON value equivalent
export function jsonValue(type: SlideQuestionType): number {
  switch (type) {
    case SlideQuestionType.poll:
      return 0
    case SlideQuestionType.multipleChoice:
      return 1
    case SlideQuestionType.title:
      return 2
    case SlideQuestionType.customPoll:
      return 3
    case SlideQuestionType.sorting:
      return 4
    case SlideQuestionType.talkTrack:
      return 5
    case SlideQuestionType.moderatorBot:
      return 6
    case SlideQuestionType.moderationTask:
      return 7
    case SlideQuestionType.tutorBot:
      return 8
    case SlideQuestionType.graderBot:
      return 9
    case SlideQuestionType.sortableStringList:
      return 10
    case SlideQuestionType.displayModerationTask:
      return 11
    case SlideQuestionType.popUpInstructions:
      return 12
    case SlideQuestionType.rolePlayBot:
      return 13
    case SlideQuestionType.formCompletionBot:
      return 14
    case SlideQuestionType.interactiveFormElement:
      return 15
    case SlideQuestionType.aiAgent:
      return 16
    case SlideQuestionType.suggestedTimer:
      return 17
    default:
      throw new Error('Unhandled SlideQuestionType')
  }
}

// Function to check compatible slide types
// todo(ashold12): this isn't being used anywhere
export function compatibleSlideTypes(type: SlideQuestionType): string[] {
  switch (type) {
    case SlideQuestionType.poll:
    case SlideQuestionType.customPoll:
      return ['interactivePoll']
    case SlideQuestionType.multipleChoice:
    case SlideQuestionType.sorting:
      return ['interactiveQuiz', 'soloQuiz']
    case SlideQuestionType.title:
    case SlideQuestionType.suggestedTimer:
      return ['everyType'] // Assuming everyType symbolically represents all types.
    case SlideQuestionType.talkTrack:
    case SlideQuestionType.moderatorBot:
    case SlideQuestionType.moderationTask:
    case SlideQuestionType.displayModerationTask:
    case SlideQuestionType.tutorBot:
    case SlideQuestionType.graderBot:
    case SlideQuestionType.rolePlayBot:
    case SlideQuestionType.formCompletionBot:
    case SlideQuestionType.interactiveFormElement:
    case SlideQuestionType.aiAgent:
      return []
    case SlideQuestionType.popUpInstructions:
    case SlideQuestionType.sortableStringList:
      return ['sortedSoloInput', 'sortedGroupInteractive', 'sortedGroupInput']
  }
}

// Function to check group quiz compatibility
export function isGroupQuizCompatible(type: SlideQuestionType): boolean {
  return type === SlideQuestionType.sorting
}
